import type { ReactNode } from 'react';
import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import type { Column } from 'react-table';
import { FuzzyFilter } from 'atom-components/table-outdated/filters/fuzzy-text-filter';
import { SelectFilter } from 'atom-components/table-outdated/filters/select-filter';
import type { Filters, Sort } from 'atom-components/table-outdated/types';
import StatusBadge from 'feature-components/badges/status-badge/status-badge';
import EthnicityDisplay from 'feature-components/display/ethnicity-display';
import GenderDisplay from 'feature-components/display/gender-display';
import YearLevel from 'feature-components/display/year-level';
import type { BaseEthnicity } from 'services/assist-api/entities/ethnicity/ethnicities';
import { useEthnicities } from 'services/assist-api/entities/ethnicity/ethnicities';
import type { BaseEthnicityGroup } from 'services/assist-api/entities/ethnicity/ethnicity-groups';
import { useEthnicityGroups } from 'services/assist-api/entities/ethnicity/ethnicity-groups';
import type { BaseGender } from 'services/assist-api/entities/gender/genders';
import { useGenders } from 'services/assist-api/entities/gender/genders';
import type { BaseGroup } from 'services/assist-api/entities/group/groups';
import { useGroups } from 'services/assist-api/entities/group/groups';
import type { BaseLevel } from 'services/assist-api/entities/level/levels';
import { useEnabledLevels } from 'services/assist-api/entities/level/levels';
import type { ROLE } from 'services/assist-api/entities/role/roles';
import { transformStatus } from 'services/assist-api/entities/test-instance-response/test-instance-response';
import type { BaseTestInstanceToken } from 'services/assist-api/entities/token/types';
import { useAuthCustomer } from 'services/providers/auth.hooks';
import ClassesDisplay from './classes-display';

export type ColumnHideOptions = {
  hideNameColumn?: boolean;
  hideLevelColumn?: boolean;
  hideClassColumn?: boolean;
  hideGenderColumn?: boolean;
  hideEthnicityColumn?: boolean;
  hideFilters?: boolean;
  isAssessmentUser?:
    | {
        hideTokenColumn?: boolean;
        hideStatusColumn?: boolean;
      }
    | boolean;
};

export type MinUser = {
  lastName: string;
  firstName: string;
  genderId: number;
  ethnicityIds: number[];
  groupIds: number[];
  levelId: number;
  token?: BaseTestInstanceToken;
  response?: { status?: string };
  roles?: { roleId: number; roleName: ROLE }[];
};

export type TableProps = {
  actionRender?(row): ReactNode;
  statusOptions?: (string | undefined)[];
  getUserLink(id: number): string;
} & ColumnHideOptions;

export const useTableProps = <User extends MinUser>({
  getUserLink,
  actionRender,
  statusOptions,
  hideNameColumn,
  hideLevelColumn,
  hideClassColumn,
  hideGenderColumn,
  hideEthnicityColumn,
  hideFilters,
  isAssessmentUser
}: TableProps): {
  columns: readonly Column<User>[];
  sortBy: Sort;
  hiddenColumns: string[];
  filterBy: Filters;
} => {
  const hideStatusColumn =
    typeof isAssessmentUser === 'boolean' || isAssessmentUser === undefined
      ? !isAssessmentUser
      : isAssessmentUser?.hideStatusColumn;
  const hideTokenColumn =
    typeof isAssessmentUser === 'boolean' || isAssessmentUser === undefined
      ? !isAssessmentUser
      : isAssessmentUser?.hideTokenColumn;

  const levels = useEnabledLevels();
  const id = useAuthCustomer()?.id || 0;

  const groups = useGroups({ filters: { customer: id } }).data?.json;

  const genders: BaseGender[] = useGenders().data?.json;
  const ethnicities = useEthnicities().data?.json;
  const ethnicityGroups = useEthnicityGroups().data?.json;

  const ethnicityOptions = useMemo(
    () =>
      ethnicityGroups?.map((g) => ({
        ...g,
        options: ethnicities?.filter((e) => e.ethnicityGroup.id === g.id) || []
      })),
    [ethnicityGroups, ethnicities]
  );

  const columns: readonly Column<User>[] = useMemo(
    () => [
      {
        Header: 'Name',
        id: 'name',
        Cell: ({ value, row }) => <Link to={getUserLink(row.original.id)}>{value}</Link>,
        accessor: (row) => `${row.firstName} ${row.lastName}`,
        ...(hideFilters ? {} : FuzzyFilter),
        sortType: (a, b) =>
          b.original.lastName?.localeCompare(a.original.lastName, undefined, {
            sensitivity: 'base'
          }),
        width: 200
      } as unknown as Column<User>,
      {
        Header: 'Gender',
        accessor: 'genderId',
        Cell: ({ value }) => <GenderDisplay genderId={value} />,
        width: 100,
        ...((hideFilters
          ? {}
          : SelectFilter({
              options: genders,
              getOptionLabel: (o) => o?.name,
              getOptionValue: (o) => String(o?.id),
              isMulti: true,
              isClearable: false,
              placeholder: '...'
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
            })) as any),
        disableSortBy: true
      },
      {
        Header: 'Ethnicity',
        accessor: 'ethnicityIds',
        Cell: ({ value }) => <EthnicityDisplay ethnicityIds={value} />,
        width: 150,
        ...(hideFilters
          ? {}
          : SelectFilter<BaseEthnicity, BaseEthnicityGroup & { options: BaseEthnicity[] }>({
              options: ethnicityOptions,
              getOptionLabel: (o) => o?.name,
              getOptionValue: (o) => String(o?.id),
              formatGroupLabel: (data) => <span>{data.name}</span>,
              isMulti: true,
              isClearable: false
            })),
        disableSortBy: true
      },
      {
        Header: 'Class',
        accessor: 'groupIds',
        Cell: ({ value }) => <ClassesDisplay classIds={value || []} />,
        width: 100,
        ...(hideFilters
          ? {}
          : SelectFilter<BaseGroup>({
              options: groups,
              getOptionLabel: (o) => o?.name,
              getOptionValue: (o) => String(o?.id),
              isMulti: true,
              isClearable: false,
              placeholder: '...'
            })),
        disableSortBy: true
      },
      {
        Header: 'Year Level',
        accessor: 'levelId',
        Cell: ({ value }) => <YearLevel levelId={value} />,
        width: 90,
        ...(hideFilters
          ? {}
          : SelectFilter<BaseLevel>({
              options: levels,
              getOptionValue: (l) => String(l.id),
              formatOptionLabel: (level) => <YearLevel levelId={level.id} />,
              isMulti: true,
              isClearable: false,
              placeholder: '...'
            }))
      },

      ...((isAssessmentUser
        ? [
            {
              Header: 'Token',
              id: 'token',
              accessor: (row) => row.token?.tokenValue,
              ...(hideFilters ? {} : FuzzyFilter),
              disableSortBy: true,
              width: 80
            }
          ]
        : []) as unknown as Column<User>[]),
      {
        Header: 'Status',
        id: 'status',
        accessor: (row) => transformStatus(row.response?.status),
        Cell: ({ value }) => <StatusBadge>{value}</StatusBadge>,
        ...(!hideFilters && (statusOptions?.length || 0) > 1
          ? SelectFilter({
              options: statusOptions?.map(transformStatus),
              formatOptionLabel: (o) => <StatusBadge>{o}</StatusBadge>,
              getOptionValue: (o) => o,
              getOptionLabel: (o) => o,
              isClearable: false,
              isMulti: true,
              placeholder: '...'
            })
          : {}),
        disableSortBy: true,
        width: 100
      } as unknown as Column<User>,
      ...((actionRender
        ? [
            {
              Header: 'Actions',
              id: 'actions',
              Cell: ({ row: { original } }) => actionRender(original),
              className: 'table-actions',
              width: 120
            }
          ]
        : []) as Column<User>[])
    ],
    [
      actionRender,
      groups,
      ethnicityOptions,
      genders,
      hideFilters,
      isAssessmentUser,
      levels,
      statusOptions,
      getUserLink
    ]
  );

  const sortBy = useMemo(() => [{ id: 'organisationName' }], []);
  const hiddenColumns = useMemo(
    () => [
      ...(hideNameColumn ? ['name'] : []),
      ...(hideLevelColumn ? ['levelId'] : []),
      ...(hideClassColumn ? ['groupIds'] : []),
      ...(hideGenderColumn ? ['genderId'] : []),
      ...(hideEthnicityColumn ? ['ethnicityIds'] : []),
      ...(hideStatusColumn ? ['status'] : []),
      ...(hideTokenColumn ? ['token'] : [])
    ],
    [
      hideNameColumn,
      hideLevelColumn,
      hideClassColumn,
      hideGenderColumn,
      hideEthnicityColumn,
      hideStatusColumn,
      hideTokenColumn
    ]
  );
  const filterBy = useMemo(() => [], []);
  return { columns, sortBy, hiddenColumns, filterBy };
};
